import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * GET /api/flow — "Where your deals come from".
 *
 * One graph per rep agreement: what it authorised, what that grew into, and
 * where it stopped. Assembled server-side (see flow.service.ts) so the page
 * doesn't have to stitch four list endpoints together in the browser.
 *
 * Keep shapes in sync with `repsity-service/src/dtos/flow.dto.ts`.
 */

export type FlowNodeKind =
  | "rep"
  | "need"
  | "listing"
  | "parent"
  | "child"
  | "property";

export interface FlowNode {
  id: string;
  kind: FlowNodeKind;
  abbr: string;
  label: string;
  sub: string;
  href: string | null;
  big?: boolean;
  /** Exists but hasn't moved past the stall threshold. */
  stalled?: boolean;
  /** Passed, lost, or blocked behind something upstream. */
  dead?: boolean;
}

export interface FlowLink {
  a: string;
  b: string;
  dead?: boolean;
}

export interface FlowRead {
  title: string;
  body: string;
}

export interface FlowClient {
  id: string;
  name: string;
  side: "buy" | "sell";
  status: string;
  nodes: FlowNode[];
  links: FlowLink[];
  reads: FlowRead[];
}

export const flowApi = createApi({
  ...createBaseApiWithAuth({ reducerPath: "flowApi", baseUrl: "/api/flow" }),
  reducerPath: "flowApi",
  tagTypes: ["Flow"],
  endpoints: (builder) => ({
    getFlow: builder.query<{ data: { clients: FlowClient[] } }, void>({
      query: () => "",
      providesTags: ["Flow"],
    }),
  }),
});

export const { useGetFlowQuery } = flowApi;
